import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiFetch } from '@jupi/ui'
import type {
  Course,
  CourseDetail,
  CreateTrainingFlightDraft,
  DirectoryInstructor,
  Enrolment,
  EnrolmentProgress,
  Manual,
  MemberLicence,
  TrainingFlight,
} from '@jupi/shared'

// The flight school's reads for the member app. Management (course
// privileges, type ratings, manual uploads) lives in the admin app.

export function useCourses() {
  return useQuery({
    queryKey: ['school', 'courses'],
    queryFn: () => api.get<{ courses: Course[] }>('/api/school/courses').then((r) => r.courses),
  })
}

export function useCourse(courseCode: string | undefined) {
  return useQuery({
    queryKey: ['school', 'course', courseCode],
    queryFn: () => api.get<{ course: CourseDetail }>(`/api/school/courses/${courseCode}`).then((r) => r.course),
    enabled: Boolean(courseCode),
  })
}

export function useInstructors() {
  return useQuery({
    queryKey: ['school', 'instructors'],
    queryFn: () => api.get<{ instructors: DirectoryInstructor[] }>('/api/school/instructors').then((r) => r.instructors),
  })
}

export function useManuals() {
  return useQuery({
    queryKey: ['school', 'manuals'],
    queryFn: () => api.get<{ manuals: Manual[] }>('/api/school/manuals').then((r) => r.manuals),
  })
}

// Everyday names for course codes, as the club says them.
export const COURSE_SHORT: Record<string, string> = {
  PPL_A: 'PPL',
  LAPL_A: 'LAPL',
  NF_A: 'NF',
  BIR: 'BIR',
  AEROBATIC: 'Aerobatic',
}

export function useEnrolments(params: { studentUserId?: string } = {}) {
  const query = params.studentUserId ? `?studentUserId=${encodeURIComponent(params.studentUserId)}` : ''
  return useQuery({
    queryKey: ['school', 'enrolments', params.studentUserId ?? 'all'],
    queryFn: () => api.get<{ enrolments: Enrolment[] }>(`/api/school/enrolments${query}`).then((r) => r.enrolments),
  })
}

export function useProgress(enrolmentId: number) {
  return useQuery({
    queryKey: ['school', 'progress', enrolmentId],
    queryFn: () => api.get<EnrolmentProgress>(`/api/school/enrolments/${enrolmentId}/progress`),
    enabled: Number.isInteger(enrolmentId),
  })
}

export function useTrainingFlights(enrolmentId: number) {
  return useQuery({
    queryKey: ['school', 'flights', enrolmentId],
    queryFn: () =>
      api.get<{ flights: TrainingFlight[] }>(`/api/school/enrolments/${enrolmentId}/flights`).then((r) => r.flights),
    enabled: Number.isInteger(enrolmentId),
  })
}

export function useLicencesOf(userId: string | undefined) {
  return useQuery({
    queryKey: ['licences', userId],
    queryFn: () => api.get<{ licences: MemberLicence[] }>(`/api/licences/${userId}`).then((r) => r.licences),
    enabled: Boolean(userId),
  })
}

// Every write refreshes the enrolment's progress and flights: progress is
// computed from them on the server, so there is nothing to patch locally.
export function useTrainingMutations(enrolmentId: number) {
  const queryClient = useQueryClient()
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['school', 'progress', enrolmentId] }),
      queryClient.invalidateQueries({ queryKey: ['school', 'flights', enrolmentId] }),
      queryClient.invalidateQueries({ queryKey: ['school', 'lessonBookings', enrolmentId] }),
    ])
  const base = `/api/school/enrolments/${enrolmentId}`
  return {
    logFlight: useMutation({
      mutationFn: (flight: CreateTrainingFlightDraft) =>
        api.post<{ flight: TrainingFlight }>(`${base}/flights`, flight).then((r) => r.flight),
      onSuccess: refresh,
    }),
    deleteFlight: useMutation({
      mutationFn: (flightId: number) => api.delete(`/api/school/flights/${flightId}`),
      onSuccess: refresh,
    }),
    attach: useMutation({
      mutationFn: ({ flightId, file }: { flightId: number; file: File }) => {
        const form = new FormData()
        form.append('file', file)
        return apiFetch(`/api/school/flights/${flightId}/attachments`, { method: 'POST', body: form })
      },
      onSuccess: refresh,
    }),
    groundCompleted: useMutation({
      mutationFn: ({ lessonId, completed, confirm }: { lessonId: number; completed: boolean; confirm?: boolean }) =>
        completed
          ? api.put(`${base}/lessons/${lessonId}/completed`, {})
          : api.delete(`${base}/lessons/${lessonId}/completed${confirm ? '?confirm=true' : ''}`),
      onSuccess: refresh,
    }),
    preSolo: useMutation({
      mutationFn: (input: { theoryModule1Completed?: boolean; competenceAssessed?: boolean }) =>
        api.put(`${base}/pre-solo-check`, input),
      onSuccess: refresh,
    }),
    headTheory: useMutation({
      mutationFn: (input: { headTheoryOk?: boolean; headTheoryPercentage?: number | null }) => api.put(`${base}/theory`, input),
      onSuccess: refresh,
    }),
  }
}

export interface LessonBooking {
  bookingId: number
  registration: string
  startsAt: string
  endsAt: string
  instructorUserId: string | null
  instructorName: string | null
  lessonId: number | null
  lessonNumber: string | null
  lessonTitle: string | null
  flightLogged: boolean
}

export function useLessonBookings(enrolmentId: number) {
  return useQuery({
    queryKey: ['school', 'lessonBookings', enrolmentId],
    queryFn: () =>
      api.get<{ bookings: LessonBooking[] }>(`/api/school/enrolments/${enrolmentId}/bookings`).then((r) => r.bookings),
    enabled: Number.isInteger(enrolmentId),
  })
}
